import apiClient from "./client";
import type {
  NotificationListResponse,
  NotificationResponse,
  UnreadCountResponse,
  MarkAllReadResponse,
  SendAnnouncementRequest,
  AnnouncementSentResponse,
} from "@/types/user";

// ─── List Notifications ──────────────────────────────────────────────────────

/**
 * List notifications for the current user with pagination.
 * Supports filtering by `unread_only` and `notification_type`.
 *
 * Maps to `GET /api/notifications?page=1&per_page=20&unread_only=false`
 */
export async function getNotifications(
  params: {
    page?: number;
    per_page?: number;
    unread_only?: boolean;
    notification_type?: string;
  } = {},
): Promise<NotificationListResponse> {
  const query: Record<string, string | number | boolean> = {};

  if (params.page != null) query.page = params.page;
  if (params.per_page != null) query.per_page = params.per_page;
  if (params.unread_only != null) query.unread_only = params.unread_only;
  if (params.notification_type) query.notification_type = params.notification_type;

  const { data } = await apiClient.get<NotificationListResponse>(
    "/notifications",
    { params: query },
  );
  return data;
}

// ─── Unread Count ────────────────────────────────────────────────────────────

/**
 * Get the unread notification count for the current user.
 * Lightweight endpoint for polling or SSE reconnection fallback.
 *
 * Maps to `GET /api/notifications/unread-count`
 */
export async function getUnreadCount(): Promise<number> {
  const { data } = await apiClient.get<UnreadCountResponse>(
    "/notifications/unread-count",
  );
  return data.unread_count;
}

// ─── Mark as Read ────────────────────────────────────────────────────────────

/**
 * Mark a single notification as read.
 * Validates that the notification belongs to the authenticated user.
 *
 * Maps to `PUT /api/notifications/:id/read`
 */
export async function markAsRead(
  notificationId: string,
): Promise<NotificationResponse> {
  const { data } = await apiClient.put<NotificationResponse>(
    `/notifications/${notificationId}/read`,
  );
  return data;
}

// ─── Mark All as Read ────────────────────────────────────────────────────────

/**
 * Mark all of the current user's notifications as read.
 * Returns the number of notifications that were updated.
 *
 * Maps to `PUT /api/notifications/read-all`
 */
export async function markAllAsRead(): Promise<MarkAllReadResponse> {
  const { data } = await apiClient.put<MarkAllReadResponse>(
    "/notifications/read-all",
  );
  return data;
}

// ─── Admin: Send Announcement ────────────────────────────────────────────────

/**
 * Send a system announcement notification to all active users (admin only).
 * Requires bilingual title and body fields (EN and FR).
 *
 * Maps to `POST /api/admin/notifications`
 */
export async function sendAnnouncement(
  payload: SendAnnouncementRequest,
): Promise<AnnouncementSentResponse> {
  const { data } = await apiClient.post<AnnouncementSentResponse>(
    "/admin/notifications",
    payload,
  );
  return data;
}
